import { getMapContents } from '../components/map/Map';
import {
  WX_ID_AMEYUKI,
  WX_ID_HIMAWARI,
  WX_ID_RADAR,
  WX_ID_TIME_SCOPE_COMPOSITE_RADAR,
  WX_ID_TROPICALCYCLONE,
} from '../store/WeatherContentsState';
import { MAP_ID_COPILOT } from "../store/MapContentsState";

const debug = false;

/**
 * 設定オブジェクトをコンテンツに適用するコア処理
 * 初期化時と動的切り替え時の両方で使用される共通ロジック
 *
 * @param {string} contentsID - コンテンツの識別子
 * @param {Object|boolean} settingsState - 適用する設定状態
 * @param {Object|boolean} disabledState - 無効化状態（省略可）
 * @returns {Promise<boolean>} コンテンツの表示状態
 */
export const applyContentsSettings = async (contentsID, settingsState, disabledState = null) => {
  const contents = getMapContents()[contentsID];

  if (!contents) {
    console.warn(`Contents not found: ${contentsID}`);
    return false;
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      /**
       * 無効化された項目をチェックし、無効化されている場合はfalseに設定
       * 再帰的にオブジェクトを走査して、無効化状態と照合する
       *
       * @param {Object|boolean} state - チェック対象の設定状態
       * @returns {Object|boolean} 無効化チェック後の状態
       */
      const checkDisabled = (state) => {
        if (state instanceof Object) {
          const newState = {};
          const keys = Object.keys(state);
          for (let i = 0; i < keys.length; i++) {
            const k = keys[i];
            const v = state[k];

            // 関数定義は常に保持
            if (['function', 'function_option'].includes(k)) {
              newState[k] = v;
              continue;
            }

            /* 配列の場合はdisabledStateとの比較をスキップ
             * 現状arrayでdisabledStateと比較するケースはないので
             * valueがarrayの場合はチェックしない*/
            if (Array.isArray(v)) {
              newState[k] = v;
              continue;
            }

            // オブジェクトの場合は再帰的にチェック
            if (v instanceof Object) {
              newState[k] = checkDisabled(v);
            } else if (typeof v === 'boolean') {
              // boolean値の場合、無効化されていればfalseに設定
              if (disabledState instanceof Object && disabledState[k]) {
                debug && console.log(`${contentsID} - ${k} - ${v} --- disabled`);
                newState[k] = false;
              } else {
                newState[k] = v;
              }
            } else if (typeof v === 'string') {
              // 文字列値の場合、無効化されていればスキップ（キーとして設定しない）
              if (disabledState instanceof Object && disabledState[v]) {
                debug && console.log(`${contentsID} - ${k} - ${v} --- disabled(skip)`);
              } else {
                newState[k] = v;
              }
            } else {
              // その他の型はそのまま設定
              newState[k] = v;
            }
          }
          return newState;
        } else {
          return state;
        }
      };

      /**
       * 設定を解析してコンテンツの表示状態を設定する
       * ネストレベルに応じて適切なメソッドを呼び出す
       *
       * @param {Object|boolean} state - 解析対象の設定状態
       * @param {number} nest - ネストレベル（0: ルート, 1以上: 子階層）
       * @param {string|null} parent - 親キー名
       * @returns {Promise<void>}
       */
      const parseSetting = async (state, nest, parent) => {
        let enable = false;

        if (state instanceof Object) {
          // 設定オブジェクトから制御用のプロパティを抽出
          let proc = null; // 実行する関数名
          let proc_opt = null; // 関数のオプション（引数の順序など）
          let value = null; // 設定値
          let processed = false; // 処理済みフラグ

          if ('enable' in state) {
            enable = !!state.enable;
          }
          if ('function' in state) {
            proc = state.function;
          }
          if ('function_option' in state) {
            proc_opt = state.function_option;
          }
          if ('value' in state) {
            value = state.value;
          }

          // 各プロパティを走査して処理
          const keys = Object.keys(state);
          for (let i = 0; i < keys.length; i++) {
            const k = keys[i];
            const v = state[k];

            // 制御用プロパティはスキップ
            if (['enable', 'function', 'function_option', 'value'].includes(k)) {
              continue;
            }

            if (typeof v === 'boolean') {
              if (nest === 0) {
                // ルートレベル：コンテンツの表示/非表示を設定
                debug && console.log(`${contentsID} - ${k} - ${v}`);
                if (typeof contents.setVisibleWithType === 'function') {
                  await contents.setVisibleWithType(v, k);
                } else if (contents.drawLayer.length >= 2) {
                  await contents.drawLayer(v, k);
                } else if (contents.setVisible.length >= 2) {
                  await contents.setVisible(v, k);
                } else {
                  debug && console.warn('function not found.');
                }
              } else {
                // 子階層：指定された関数を実行
                if (typeof contents[proc] === 'function') {
                  debug && console.log(`${contentsID} - ${k} - ${v}`);
                  if (value !== null) {
                    await contents[proc](value);
                  } else {
                    // proc_optが"reverse"の場合は引数の順序を逆にする
                    if (proc_opt === 'reverse') {
                      await contents[proc](k, v);
                    } else {
                      await contents[proc](v, k);
                    }
                  }
                  processed = true;
                } else {
                  debug && console.log(`${contentsID} - ${k} - ${v} no func`);
                }
              }
            } else if (v instanceof Object) {
              // ネストされたオブジェクトを再帰的に処理
              await parseSetting(v, nest + 1, k);
            }

            // enableプロパティがない場合、値がtrueならenableをtrueに設定
            if (!('enable' in state) && !!v) {
              enable = true;
            }
          }

          // 子要素の処理が行われなかった場合、親レベルで処理
          if (!processed) {
            if (nest === 0) {
              // ルートレベル：enable値をresolve
              debug && console.log(`${contentsID} - ${enable}`);
              // ContentsSet対応: enable:falseかつ表示中の場合、drawLayer(false)
              if (enable === false && contents.visible === true && typeof contents.drawLayer === 'function' && contents.drawLayer.length === 1) {
                debug && console.log(`${contentsID} - drawLayer(${enable})`);
                await contents.drawLayer(false);
              }
              resolve(enable);
            } else if (nest === 1) {
              // ネストレベル1：適切なメソッドを呼び出し
              if (proc !== null) {
                if (typeof contents[proc] === 'function') {
                  if (value !== null) {
                    debug && console.log(`${contentsID} - ${parent} - ${JSON.stringify(value)}`);
                    await contents[proc](value);
                  } else {
                    debug && console.log(`${contentsID} - ${parent} - ${enable}`);
                    if (proc_opt === 'reverse') {
                      await contents[proc](parent, enable);
                    } else {
                      await contents[proc](enable, parent);
                    }
                  }
                } else {
                  console.warn(`function not defined.[${proc}]`);
                }
              } else if (typeof contents.setVisibleWithType === 'function') {
                debug && console.log(`${contentsID} - ${parent} - ${enable}`);
                await contents.setVisibleWithType(enable, parent);
              } else if (contents.drawLayer.length >= 2) {
                debug && console.log(`${contentsID} - ${parent} - ${enable}`);
                await contents.drawLayer(enable, parent);
              } else {
                if (!(state instanceof Object)) {
                  console.warn('function not found.');
                }
              }
            } else {
              // ネストレベル2以上：指定された関数を実行
              debug && console.log(`${contentsID} - ${parent} - ${enable}`);
              if (typeof contents[proc] === 'function') {
                if (value !== null) {
                  await contents[proc](value);
                } else {
                  if (proc_opt === 'reverse') {
                    await contents[proc](parent, enable);
                  } else {
                    await contents[proc](enable, parent);
                  }
                }
              } else {
                console.warn('function not found.');
              }
            }
          }
        } else {
          // オブジェクトでない場合（boolean値）、そのままresolve
          enable = state === true;
          debug && console.log(`${contentsID} - ${enable}`);
          resolve(enable);
        }
      };

      // 無効化チェックを適用（disabledStateが提供された場合のみ）
      const _state = disabledState !== null ? checkDisabled(settingsState) : settingsState;

      // 特定のコンテンツに対する特殊な検証処理
      applyContentSpecificValidation(contentsID, _state);

      debug && console.log(`after checkDisabled: ${JSON.stringify(_state)}`);

      // 設定を解析して適用
      parseSetting(_state, 0, null);
    });
  });
};

/**
 * 特定のコンテンツに対する特殊な検証ロジック
 * コンテンツタイプに応じてstateを修正する
 *
 * @param {string} contentsID - コンテンツの識別子
 * @param {Object} state - 検証対象の設定状態（参照渡しで修正される）
 */
const applyContentSpecificValidation = (contentsID, state) => {
  // ひまわり：DATA_TYPEとvalueが存在しない場合は無効化
  if (
    contentsID === WX_ID_HIMAWARI &&
    (!('DATA_TYPE' in state) || !('value' in state['DATA_TYPE']))
  ) {
    state.enable = false;
    delete state['DATA_TYPE'];
  }
  // レーダー：すべてのデータソースが無効の場合は無効化
  else if (
    contentsID === WX_ID_RADAR &&
    Object.keys(state)
      .filter((k) => ['Japan', 'Korea', 'Taiwan', 'NOWCAST', 'XRAIN', 'PRCINT'].includes(k))
      .map((k) => state[k])
      .filter((v) => v).length === 0
  ) {
    state.enable = false;
  }
  // 台風：すべてのデータソースが無効の場合は無効化
  else if (
    contentsID === WX_ID_TROPICALCYCLONE &&
    Object.keys(state)
      .filter((k) => ['WNIGlobalTC', 'WNITyphoon', 'JMA', 'JTWC'].includes(k))
      .map((k) => state[k])
      .filter((v) => v).length === 0
  ) {
    state.enable = false;
  }
  // 雨雪判別・時間範囲合成レーダー：enableプロパティがない場合は無効化
  else if (contentsID === WX_ID_AMEYUKI && !state.hasOwnProperty('enable')) {
    state.enable = false;
  } else if (contentsID === WX_ID_TIME_SCOPE_COMPOSITE_RADAR && !state.hasOwnProperty('enable')) {
    state.enable = false;
  }
  /*
  MAP_ID_COPILOTのintervalHourの設定形式変更に伴うコンバート処理
     旧format:
      "MAP_ID_COPILOT":{"intervalHour": "1"}
     新format:
      "MAP_ID_COPILOT":{"intervalHour": {"function": "setIntervalHour", "value":"1"}}
   */
  else if (contentsID === MAP_ID_COPILOT && typeof state.intervalHour === 'string') {
    state.intervalHour = {
      function: 'setIntervalHour',
      value: state.intervalHour,
    }
  }
};
